import React from 'react';
import { Box, Tooltip } from '@mui/material';
import type { VertebralNumberingIssue } from '../services/reportService';

interface Props {
  text: string;
  issues?: VertebralNumberingIssue[];
  section: string;
}

const escapePattern = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const INLINE_BOLD = /\*\*([^*]+)\*\*/g;

const HighlightedLevelText: React.FC<Props> = ({ text, issues = [], section }) => {
  const relevant = issues.filter((issue) => issue.sections.some((item) => item.toLowerCase() === section.toLowerCase()));
  const levels = Array.from(new Set(relevant.flatMap((issue) => issue.affected_levels))).sort((a, b) => b.length - a.length);
  const pattern = levels.length > 0 ? new RegExp(`(${levels.map(escapePattern).join('|')})`, 'gi') : null;

  const highlight = (segment: string, keyPrefix: string): React.ReactNode[] => {
    if (!pattern) return [segment];
    return segment.split(pattern).map((part, index) => {
      const key = `${keyPrefix}-${index}`;
      const issue = relevant.find((candidate) => candidate.affected_levels.some((level) => level.toLowerCase() === part.toLowerCase()));
      if (!issue) return <React.Fragment key={key}>{part}</React.Fragment>;
      return (
        <Tooltip key={key} title={`${issue.message} ${issue.remediation}`.trim()} arrow>
          <Box
            component="mark"
            aria-label={`${part}: ${issue.message}`}
            sx={{
              bgcolor: issue.severity === 'blocking' ? 'error.light' : issue.severity === 'warning' ? 'warning.light' : 'info.light',
              color: 'inherit',
              borderRadius: 0.5,
              px: 0.25,
            }}
          >
            {part}
          </Box>
        </Tooltip>
      );
    });
  };

  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  for (const match of text.matchAll(INLINE_BOLD)) {
    const start = match.index ?? 0;
    if (start > cursor) nodes.push(...highlight(text.slice(cursor, start), `t${cursor}`));
    nodes.push(<strong key={`b${start}`}>{highlight(match[1], `b${start}`)}</strong>);
    cursor = start + match[0].length;
  }
  if (cursor < text.length) nodes.push(...highlight(text.slice(cursor), `t${cursor}`));

  return <>{nodes}</>;
};

export default HighlightedLevelText;
